import type {
  ButtonHTMLAttributes,
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
} from 'react'
import { useEffect, useRef, useState } from 'react'

/** Primary is a filled pill; secondary an outlined one at the control height. */
export function Button({
  children,
  kind = 'secondary',
  ...rest
}: { children: ReactNode; kind?: 'primary' | 'secondary' | 'danger' } & ButtonHTMLAttributes<HTMLButtonElement>): ReactElement {
  return (
    <button type="button" className="nw-btn" data-kind={kind} {...rest}>
      {children}
    </button>
  )
}

/** The invitation at the foot of a list that has room for one more. */
export function DashedButton({
  children,
  ...rest
}: { children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>): ReactElement {
  return (
    <button type="button" className="nw-dashed" {...rest}>
      {children}
    </button>
  )
}

/**
 * The circled question mark the mockups set beside a figure, as a control
 * rather than as a tooltip.
 *
 * A `title` is a pointer and nothing else: no keyboard reaches it, and a phone
 * has no hover to show it with — which is most of the people reading a figure
 * that needs explaining. So this is a button that says what it opens, and the
 * `title` stays beside it as the version a pointer gets for free.
 *
 * What it opens belongs to the caller. The sentence explaining a figure in a
 * table sits in the table, not in a panel floating over a column that scrolls
 * sideways on a phone.
 */
export function WhyButton({
  label,
  expanded,
  controls,
  onToggle,
}: {
  label: string
  expanded: boolean
  /** The id of the thing this opens, so a screen reader can be taken to it. */
  controls: string
  onToggle: () => void
}): ReactElement {
  return (
    <button
      type="button"
      className="nw-help nw-why"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onToggle}
    >
      ?
    </button>
  )
}

export interface Choice {
  id: string
  label: string
}

/**
 * The range pills: a trough the selected one sits in, raised on white.
 *
 * Buttons rather than links, because choosing a range is a change of state on
 * this page and not a place to go.
 */
export function SegmentedControl({
  choices,
  value,
  onChange,
  label,
}: {
  choices: readonly Choice[]
  value: string
  onChange: (id: string) => void
  label: string
}): ReactElement {
  return (
    <div className="nw-segmented" role="group" aria-label={label}>
      {choices.map((choice) => (
        <button
          key={choice.id}
          type="button"
          aria-pressed={choice.id === value}
          onClick={() => onChange(choice.id)}
        >
          {choice.label}
        </button>
      ))}
    </div>
  )
}

/**
 * A segmented control with its name beside it.
 *
 * Three of these can sit in one row at the top of a page, and a control that
 * only names itself to a screen reader leaves everyone else to work out which
 * of them is the range.
 */
export function Filter({
  label,
  choices,
  value,
  onChange,
}: {
  label: string
  choices: readonly Choice[]
  value: string
  onChange: (id: string) => void
}): ReactElement {
  return (
    <div className="nw-filter">
      <span>{label}</span>
      <SegmentedControl label={label} choices={choices} value={value} onChange={onChange} />
    </div>
  )
}

/**
 * Sub-navigation inside a page, under a 2px accent rule.
 *
 * `tab`/`tablist` rather than a row of links: these swap what is rendered in
 * place, and a screen reader that is told they are tabs will say which one of
 * how many is showing.
 *
 * Only the selected tab is in the tab order, which is what a tablist promises
 * and is why the arrow keys have to carry the rest — without them the unselected
 * tabs can be seen from a keyboard and never reached.
 */
export function Tabs({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: readonly Choice[]
  value: string
  onChange: (id: string) => void
  label: string
}): ReactElement {
  const list = useRef<HTMLDivElement>(null)
  const followFocus = useRef(false)

  useEffect(() => {
    if (!followFocus.current) return
    followFocus.current = false
    list.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus()
  }, [value])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const here = tabs.findIndex((tab) => tab.id === value)
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    const next =
      step !== 0
        ? (here + step + tabs.length) % tabs.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : -1

    const chosen = next === -1 ? undefined : tabs[next]
    if (chosen === undefined) return

    event.preventDefault()
    followFocus.current = true
    onChange(chosen.id)
  }

  return (
    <div className="nw-tabs" role="tablist" aria-label={label} ref={list} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`nw-tab-${tab.id}`}
          aria-selected={tab.id === value}
          aria-controls={`nw-panel-${tab.id}`}
          tabIndex={tab.id === value ? 0 : -1}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

const MENU_ITEMS = '[role="menuitem"], [role="menuitemradio"]'

/**
 * A button and the panel it opens, for the things a person does to their own
 * account rather than to their figures.
 *
 * The panel is a `menu`, so the arrow keys move between its entries and Tab
 * leaves it entirely; the alternative is a list of links that reads as part of
 * the page behind it and can be tabbed into while it is shut.
 */
export function Menu({
  label,
  trigger,
  children,
}: {
  label: string
  trigger: ReactNode
  /** Given the way to shut the panel, because choosing an entry is done with. */
  children: (close: () => void) => ReactNode
}): ReactElement {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const openAt = useRef<'first' | 'last'>('first')

  useEffect(() => {
    if (!open) return
    const entries = panel.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []
    const wanted = openAt.current === 'last' ? entries.length - 1 : 0
    entries[wanted]?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return

    // A pointer that lands anywhere else has chosen nothing, and a panel that
    // stays open over the page it is not about is the thing people complain of.
    const onPointer = (event: globalThis.MouseEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open])

  const shut = (returnFocus: boolean): void => {
    setOpen(false)
    if (returnFocus) button.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      shut(true)
      return
    }
    if (event.key === 'Tab') {
      setOpen(false)
      return
    }

    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0
    if (step === 0 && event.key !== 'Home' && event.key !== 'End') return

    const entries = [...(panel.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [])]
    if (entries.length === 0) return

    event.preventDefault()
    const here = entries.findIndex((entry) => entry === document.activeElement)
    const next =
      step !== 0
        ? (here + step + entries.length) % entries.length
        : event.key === 'Home'
          ? 0
          : entries.length - 1
    entries[next]?.focus()
  }

  return (
    <div className="nw-menu" ref={root} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="nw-menu-trigger"
        ref={button}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => {
          openAt.current = 'first'
          setOpen((was) => !was)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
          event.preventDefault()
          openAt.current = event.key === 'ArrowUp' ? 'last' : 'first'
          setOpen(true)
        }}
      >
        {trigger}
      </button>
      {open && (
        <div className="nw-menu-panel" role="menu" aria-label={label} ref={panel}>
          {children(() => shut(true))}
        </div>
      )}
    </div>
  )
}

export interface Series {
  id: string
  label: string
  /** The line this key names, so the legend swatch is the line itself. */
  stroke: 'solid' | 'dashed' | 'dotted'
  color: string
  title?: string
}

/**
 * How a key draws the shading it names, which is how the plot draws it: a
 * hatch rising to the right, the same hatch mirrored, or a flat wash. Every key
 * used to be the one hatch, so the legend could not say which band was which,
 * and hatched a stretch the plot washes flat.
 */
export type KeyTexture = 'rising' | 'falling' | 'flat'

/**
 * The legend, and the control that decides what is plotted.
 *
 * A key that only names a line leaves a reader with three lines they did not
 * ask for; one that turns them off lets the page start with the comparison that
 * matters and keep the third within reach. The swatch is drawn in the line's own
 * dash pattern, so the legend and the plot are the same statement.
 */
export function SeriesToggle({
  series,
  visible,
  onToggle,
  label,
  extras,
}: {
  series: readonly Series[]
  visible: ReadonlySet<string>
  onToggle: (id: string) => void
  label: string
  /** Keys that name a shading rather than a line, and cannot be switched off. */
  extras?: readonly { label: string; color: string; texture: KeyTexture; title?: string }[]
}): ReactElement {
  return (
    <div className="nw-series" role="group" aria-label={label}>
      {series.map((entry) => (
        <button
          key={entry.id}
          type="button"
          aria-pressed={visible.has(entry.id)}
          title={entry.title}
          onClick={() => onToggle(entry.id)}
        >
          <span
            className="nw-series-key"
            data-stroke={entry.stroke}
            style={{ color: entry.color } as CSSProperties}
          />
          {entry.label}
        </button>
      ))}
      {extras?.map((extra) => (
        <span className="nw-series-extra" key={extra.label} title={extra.title}>
          <span
            className="nw-hatch-key"
            data-texture={extra.texture}
            style={{ color: extra.color } as CSSProperties}
          />
          {extra.label}
        </span>
      ))}
    </div>
  )
}

export interface Destination {
  path: string
  label: string
}

/** The top-level destinations, as pills; the selected one takes a filled pill. */
export function PillNav({
  destinations,
  current,
  onNavigate,
  label,
}: {
  destinations: readonly Destination[]
  current: string
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, to: string) => void
  label: string
}): ReactElement {
  return (
    <nav className="nw-pills" aria-label={label}>
      {destinations.map((destination) => (
        <a
          key={destination.path}
          href={destination.path}
          aria-current={destination.path === current ? 'page' : undefined}
          onClick={(event) => onNavigate(event, destination.path)}
        >
          {destination.label}
          {/* The rule under a phone's tab; invisible where the pill does the work. */}
          <span className="nw-pill-rule" aria-hidden="true" />
        </a>
      ))}
    </nav>
  )
}
